// @flow
import React, {Component} from 'react';
import {
  View,
  Text,
  ScrollView,
  Image,
  Animated,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import {EmphasizedSubText} from 'trpc-react-native-ui';
import get from 'lodash/get';
import {Icon} from '../common';
import theme from '../../theme-config';
import {DESKTOP_BREAKPOINT} from '../../lib/responsive';

const DEFAULT_AUTO_SCROLL_DURATION = 3000;
const RESUME_DELAY_AFTER_MANUAL_SCROLL = 5000;
const STEP_CIRCLE_SIZE = 112;
const CONNECTOR_HEIGHT = 3;

class RequiredItemCarousel extends Component {
  scrollViewRef;
  autoScrollTimer;
  resumeTimer;
  imageAnim;
  textAnim;
  stepEnterAnim;
  progressAnim;
  progressAnimation;
  isUserScrolling;
  isProgrammaticScroll;
  animatedPage;

  constructor(props) {
    super(props);
    this.state = {
      currentPage: 0,
      containerWidth: 0,
    };
    this.scrollViewRef = React.createRef();
    this.imageAnim = new Animated.Value(1);
    this.textAnim = new Animated.Value(1);
    this.stepEnterAnim = new Animated.Value(1);
    this.progressAnim = new Animated.Value(0);
    this.progressAnimation = null;
    this.isUserScrolling = false;
    this.isProgrammaticScroll = false;
    this.animatedPage = -1;
  }

  componentDidMount() {
    this.triggerSlideIn(0);
    this.startAutoScroll();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.currentPage !== this.state.currentPage) {
      this.triggerSlideIn(this.state.currentPage);
    }
  }

  componentWillUnmount() {
    this.stopAutoScroll();
    this.clearResumeTimer();
    if (this.progressAnimation) {
      this.progressAnimation.stop();
    }
  }

  // ─── Animations ───────────────────────────────────────────────────────────

  triggerSlideIn = page => {
    if (page === this.animatedPage) return;
    this.animatedPage = page;

    this.imageAnim.setValue(0);
    this.textAnim.setValue(0);
    this.stepEnterAnim.setValue(0);

    if (this.progressAnimation) {
      this.progressAnimation.stop();
    }
    this.progressAnim.setValue(0);

    const duration =
      this.props.autoScrollDuration || DEFAULT_AUTO_SCROLL_DURATION;

    Animated.parallel([
      Animated.timing(this.imageAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: false,
      }),
      Animated.timing(this.textAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: false,
      }),
      Animated.spring(this.stepEnterAnim, {
        toValue: 1,
        friction: 5,
        tension: 200,
        useNativeDriver: false,
      }),
    ]).start();

    this.progressAnimation = Animated.timing(this.progressAnim, {
      toValue: 1,
      duration: Math.round(duration / 3),
      useNativeDriver: false,
    });
    this.progressAnimation.start();
  };

  // ─── Auto-scroll ──────────────────────────────────────────────────────────

  startAutoScroll = () => {
    this.stopAutoScroll();
    const duration =
      this.props.autoScrollDuration || DEFAULT_AUTO_SCROLL_DURATION;
    this.autoScrollTimer = setInterval(() => {
      if (this.isUserScrolling) return;
      const {currentPage, containerWidth} = this.state;
      if (containerWidth === 0) return;
      const totalPages = this.props.items.length;
      const nextPage = (currentPage + 1) % totalPages;
      if (containerWidth >= DESKTOP_BREAKPOINT) {
        this.setState({currentPage: nextPage});
      } else {
        this.scrollToPage(nextPage);
      }
    }, duration);
  };

  stopAutoScroll = () => {
    if (this.autoScrollTimer) {
      clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }
  };

  clearResumeTimer = () => {
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  };

  scheduleResumeAutoScroll = () => {
    this.clearResumeTimer();
    this.resumeTimer = setTimeout(() => {
      this.isUserScrolling = false;
      this.startAutoScroll();
    }, RESUME_DELAY_AFTER_MANUAL_SCROLL);
  };

  // ─── Navigation ───────────────────────────────────────────────────────────

  scrollToPage = page => {
    const {containerWidth} = this.state;
    if (this.scrollViewRef.current && containerWidth > 0) {
      this.isProgrammaticScroll = true;
      this.scrollViewRef.current.scrollTo({
        x: page * containerWidth,
        animated: true,
      });
    }
  };

  onDotPress = page => {
    this.isUserScrolling = true;
    this.stopAutoScroll();
    this.scrollToPage(page);
    this.scheduleResumeAutoScroll();
  };

  onStepPress = page => {
    this.isUserScrolling = true;
    this.stopAutoScroll();
    this.setState({currentPage: page});
    this.scheduleResumeAutoScroll();
  };

  // ─── Scroll events ────────────────────────────────────────────────────────

  onScrollBeginDrag = () => {
    this.isUserScrolling = true;
    this.isProgrammaticScroll = false;
    this.stopAutoScroll();
    this.clearResumeTimer();
  };

  onScroll = event => {
    const {containerWidth} = this.state;
    if (containerWidth === 0) return;
    const offsetX = event.nativeEvent.contentOffset.x;
    const page = Math.round(offsetX / containerWidth);
    const totalPages = this.props.items.length;
    if (page >= 0 && page < totalPages && page !== this.state.currentPage) {
      this.setState({currentPage: page});
    }
  };

  onScrollEndDrag = () => {
    this.scheduleResumeAutoScroll();
  };

  onMomentumScrollEnd = event => {
    const {containerWidth} = this.state;
    if (containerWidth === 0) return;
    const offsetX = event.nativeEvent.contentOffset.x;
    const page = Math.round(offsetX / containerWidth);
    const totalPages = this.props.items.length;
    if (page >= 0 && page < totalPages) {
      this.setState({currentPage: page});
    }
    this.isProgrammaticScroll = false;
  };

  // ─── Layout ───────────────────────────────────────────────────────────────

  onLayout = event => {
    const {width} = event.nativeEvent.layout;
    if (width > 0 && width !== this.state.containerWidth) {
      this.setState({containerWidth: width});
    }
  };

  // ─── Carousel rendering ───────────────────────────────────────────────────

  getIllustrationConfig = iconName => {
    return get(theme, `requiredToSignUp.${iconName}`);
  };

  renderIllustration = (item, isActive) => {
    const config = this.getIllustrationConfig(item.iconName);
    if (!config || config.useIcon) {
      return (
        <Icon name={item.iconName} size={72} color={theme.secondaryColor} />
      );
    }
    return (
      <Image
        style={styles.slideImage}
        resizeMode="contain"
        source={config.image}
      />
    );
  };

  renderSlide = (item, index) => {
    const {containerWidth, currentPage} = this.state;
    const slideWidth = containerWidth > 0 ? containerWidth : '100%';
    const isActive = index === currentPage;

    const imageAnimStyle = isActive
      ? {
          opacity: this.imageAnim,
          transform: [
            {
              translateX: this.imageAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [60, 0],
              }),
            },
          ],
        }
      : {opacity: 1, transform: [{translateX: 0}]};

    const textAnimStyle = isActive
      ? {
          opacity: this.textAnim,
          transform: [
            {
              translateX: this.textAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [40, 0],
              }),
            },
          ],
        }
      : {opacity: 1, transform: [{translateX: 0}]};

    return (
      <View key={index} style={[styles.slide, {width: slideWidth}]}>
        <Animated.View style={[styles.illustrationContainer, imageAnimStyle]}>
          <View style={styles.floatingCard}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>{item.itemCount}</Text>
            </View>
            {this.renderIllustration(item, isActive)}
          </View>
        </Animated.View>
        <Animated.View style={textAnimStyle}>
          <EmphasizedSubText
            style={styles.itemDescription}
            emphStyle={styles.itemDescriptionBold}
            text={item.itemDescription}
          />
        </Animated.View>
      </View>
    );
  };

  renderDots = () => {
    const {items} = this.props;
    const {currentPage} = this.state;

    return (
      <View style={styles.dotsContainer}>
        {items.map((_, index) => (
          <TouchableOpacity
            key={index}
            onPress={() => this.onDotPress(index)}
            style={styles.dotTouchable}>
            <View
              style={[
                styles.dot,
                index === currentPage ? styles.dotActive : styles.dotInactive,
              ]}
            />
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  renderCarousel = () => (
    <>
      <ScrollView
        ref={this.scrollViewRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScrollBeginDrag={this.onScrollBeginDrag}
        onScrollEndDrag={this.onScrollEndDrag}
        onMomentumScrollEnd={this.onMomentumScrollEnd}
        onScroll={this.onScroll}
        scrollEventThrottle={16}
        style={styles.scrollView}>
        {this.props.items.map((item, index) => this.renderSlide(item, index))}
      </ScrollView>
      {this.renderDots()}
    </>
  );

  renderStepperIcon = (item, isActive, isPast) => {
    const config = this.getIllustrationConfig(item.iconName);
    if (config && !config.useIcon) {
      return (
        <Image
          style={styles.stepperImage}
          resizeMode="contain"
          source={config.image}
        />
      );
    }
    return (
      <Icon
        name={item.iconName}
        size={48}
        color={isActive || isPast ? '#0000FF' : 'rgba(0,0,255,0.3)'}
      />
    );
  };

  // ─── Stepper rendering (desktop) ──────────────────────────────────────────

  renderStepper = () => {
    const {items} = this.props;
    const {currentPage} = this.state;

    return (
      <View style={styles.stepperContainer}>
        <View style={styles.stepperRow}>
          {items.map((item, index) => {
            const isActive = index === currentPage;
            const isPast = index < currentPage;

            const circleScale = isActive
              ? this.stepEnterAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.82, 1],
                })
              : 1;

            const labelOpacity = isActive
              ? this.stepEnterAnim.interpolate({
                  inputRange: [0, 0.4, 1],
                  outputRange: [0, 0, 1],
                })
              : 1;

            return (
              <React.Fragment key={index}>
                <TouchableOpacity
                  onPress={() => this.onStepPress(index)}
                  style={styles.stepItem}
                  activeOpacity={0.75}>
                  <Animated.View
                    style={[
                      styles.stepCircle,
                      isActive
                        ? styles.stepCircleActive
                        : isPast
                        ? styles.stepCirclePast
                        : styles.stepCircleInactive,
                      {transform: [{scale: circleScale}]},
                    ]}>
                    {this.renderStepperIcon(item, isActive, isPast)}
                  </Animated.View>
                  <Animated.View style={[styles.stepLabelWrapper, {opacity: labelOpacity}]}>
                    <EmphasizedSubText
                      style={[
                        styles.stepLabel,
                        isActive
                          ? styles.stepLabelActive
                          : styles.stepLabelInactive,
                      ]}
                      emphStyle={styles.stepLabelEmph}
                      text={item.itemDescription}
                    />
                  </Animated.View>
                </TouchableOpacity>

                {index < items.length - 1 && (
                  <View style={styles.connectorWrapper}>
                    <View style={styles.connectorTrack}>
                      <Animated.View
                        style={[
                          styles.connectorFill,
                          {
                            width: isPast
                              ? '100%'
                              : isActive
                              ? this.progressAnim.interpolate({
                                  inputRange: [0, 1],
                                  outputRange: ['0%', '100%'],
                                })
                              : '0%',
                          },
                        ]}
                      />
                    </View>
                  </View>
                )}
              </React.Fragment>
            );
          })}
        </View>
      </View>
    );
  };

  // ─── Root render ──────────────────────────────────────────────────────────

  render() {
    const {containerWidth} = this.state;
    const isDesktop = containerWidth >= DESKTOP_BREAKPOINT;

    return (
      <View style={styles.container} onLayout={this.onLayout}>
        {containerWidth === 0
          ? null
          : isDesktop
          ? this.renderStepper()
          : this.renderCarousel()}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    overflow: 'hidden',
  },

  // ── Carousel ──────────────────────────────────────────────────────────────
  scrollView: {
    width: '100%',
  },
  slide: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 30,
    paddingVertical: 20,
  },
  illustrationContainer: {
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  floatingCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    paddingVertical: 44,
    paddingHorizontal: 32,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    marginTop: 12,
    shadowColor: '#0000FF',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.08,
    shadowRadius: 20,
    elevation: 4,
  },
  stepBadge: {
    position: 'absolute',
    top: -12,
    right: 16,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#0000FF',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  stepBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
    lineHeight: 14,
  },
  slideImage: {
    width: 110,
    height: 110,
  },
  itemDescription: {
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'center',
    color: theme.signUpTextColor,
    fontFamily: 'Gotham',
    maxWidth: 400,
  },
  itemDescriptionBold: {
    fontWeight: '700',
    fontSize: 18,
  },
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dotTouchable: {
    padding: 6,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  dotActive: {
    backgroundColor: theme.primaryColor,
  },
  dotInactive: {
    backgroundColor: '#DDDDDF',
  },

  // ── Stepper (desktop ≥ 640px) ─────────────────────────────────────────────
  stepperContainer: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    paddingVertical: 36,
    paddingHorizontal: 24,
  },
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepItem: {
    width: 130,
    alignItems: 'center',
    flexShrink: 0,
  },
  stepCircle: {
    width: STEP_CIRCLE_SIZE,
    height: STEP_CIRCLE_SIZE,
    borderRadius: STEP_CIRCLE_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepperImage: {
    width: 68,
    height: 68,
  },
  stepCircleActive: {
    backgroundColor: '#FFFFFF',
    borderWidth: 3,
    borderColor: '#0000FF',
  },
  stepCirclePast: {
    backgroundColor: '#FFFFFF',
    borderWidth: 3,
    borderColor: '#0000FF',
  },
  stepCircleInactive: {
    backgroundColor: 'rgba(0,0,255,0.1)',
  },
  stepLabelWrapper: {
    marginTop: 10,
    width: 130,
  },
  stepLabel: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    fontFamily: 'Gotham',
  },
  stepLabelActive: {
    color: theme.signUpTextColor,
  },
  stepLabelInactive: {
    color: 'rgba(0,0,255,0.35)',
  },
  stepLabelEmph: {
    fontWeight: '700',
  },
  connectorWrapper: {
    flex: 1,
    alignSelf: 'flex-start',
    // Centers the connector on the circles:
    // STEP_CIRCLE_SIZE / 2 - CONNECTOR_HEIGHT / 2 = 56 - 1.5 = 54.5
    marginTop: 55,
  },
  connectorTrack: {
    height: CONNECTOR_HEIGHT,
    borderRadius: CONNECTOR_HEIGHT / 2,
    backgroundColor: 'rgba(0,0,255,0.1)',
    overflow: 'hidden',
  },
  connectorFill: {
    height: '100%',
    borderRadius: CONNECTOR_HEIGHT / 2,
    backgroundColor: '#0000FF',
  },
});

export default RequiredItemCarousel;
